import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import Form from "components/common/form/Form";
import DateInput from "components/common/form/inputs/date/DateInput";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import FormActions from "components/common/form/partials/FormActions";
import HTMLCode from "components/common/HTMLCode";
import Translate from "components/common/Translate";
import PersonDropdown from "components/entities/app/person/partials/inputs/PersonDropdown";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import { uuid } from "config/app-constants";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IdName } from "types/common-types";
import { PersonType } from "types/person-types";
import { SeasonType } from "types/season-type";
import { removeAt } from "utilities/app-utilities";
import FruitCategoryMultiselectInput from "../fruit/classifications/inputs/FruitCategoryMultiselectInput";
import { ClassificationType } from "../fruit/classifications/inputs/FruitClassificationMultiselectInput";
import FruitGroupMultiselectInput from "../fruit/classifications/inputs/FruitGroupMultiselectInput";
import FruitInput from "../fruit/inputs/FruitInput";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";

export type HarvestFormWorkerType = {
    person?: PersonType,
    price?: number | string,
    id: string
}

export type HarvestItemFormType = {
    id: number | string,
    fruit?: IdName,
    fruitGroup: ClassificationType[],
    fruitCategories: ClassificationType[],
    kg?: string | number,
    cartQuantity?: string | number | null,
    hideFromMultiUpdate?: boolean | null
}

export type HarvestFormType = {
    id: number,
    name?: string,
    season?: SeasonType,
    date?: Date,
    note?: string,
    workers: HarvestFormWorkerType[],
    harvests: HarvestItemFormType[]
}

type HarvestFormFieldsPropsType = {
    model: HarvestFormType | undefined,
    isTemplate?: boolean,
    name: string,
    onSubmit: (model: HarvestFormType) => Promise<void>,
}

const HarvestFormFields = (props: HarvestFormFieldsPropsType) => {
    const [harvestError, setHarvestError] = useState<boolean>(false);
    const [workerError, setWorkerError] = useState<boolean>(false);
    const [model, setModel] = useState<HarvestFormType>({
        ...props.model || {
            workers: [{
                id: uuid()
            }],
            fruitGroup: [],
            id: 0,
            harvests: [{
                id: uuid(),
                fruitGroup: [],
                fruitCategories: []
            }]
        }
    });
    const [t] = useTranslation();

    // #region workers
    const addWorker = () => {
        const workers = model.workers || [];
        workers.push({ id: uuid() });
        setModel({ ...model, workers })
    }

    const setWorker = (w: Partial<HarvestFormWorkerType>, index: number) => {
        model.workers![index] = {
            ...model.workers![index],
            ...w
        };

        setModel({ ...model });
    }

    const removeWorker = (index: number) => {
        const workers = removeAt(model.workers!, index);
        if(!props.isTemplate && workers.length === 0) {
            workers.push({ id: uuid() });
        }
        setModel({ ...model, workers });
    }
    // #endregion workers

    // #region harvests
    const addHarvest = () => {
        const harvests = model.harvests || [];
        harvests.push({
            id: uuid(),
            fruitCategories: [],
            fruitGroup: []
        });
        model.harvests = harvests;
        setModel({ ...model, harvests })
    }

    const setHarvest = (h: Partial<HarvestItemFormType>, index: number) => {
        model.harvests![index] = {
            ...model.harvests![index],
            ...h
        };

        setModel({ ...model });
    }

    const removeHarvests = (index: number) => {
        const harvests = removeAt(model.harvests!, index);
        if(!props.isTemplate && harvests.length === 0) {
            harvests.push({
                id: uuid(),
                fruitCategories: [],
                fruitGroup: [],
                kg: '',
                fruit: undefined,
                cartQuantity: ''
            });
        }
        setModel({ ...model, harvests });
    }
    // #endregion harvests

    // #region submit
    const onSubmit = () => {
        if(!props.isTemplate) {
            if(model.harvests.length === 0) {
                setHarvestError(true);
                return;
            }

            if(model.workers.length === 0) {
                setWorkerError(true);
                return;
            }
        }

        props.onSubmit(model);
    };
    // #endregion submit

    return (
        <Form name={props.name} onSubmit={() => onSubmit()}>
            <fieldset>
                <SeasonDropdown
                    value={model.season}
                    required={!props.isTemplate}
                    onChange={c => setModel({ ...model, season: c })}
                />
                <Input
                    value={model.name}
                    required
                    label="fields.name.name"
                    onChange={v => setModel({ ...model, name: v })}
                />
                <DateInput
                    name="date"
                    label="fields.date.name"
                    required={!props.isTemplate}
                    value={model.date}
                    onChange={v => setModel({ ...model, date: v! })}
                />
            </fieldset>
            <fieldset>
                <h2 className={`${harvestError ? 'text-danger' : ''}`}>Harvest data</h2>
                <hr />
                {model.harvests.map((h, i) => {
                    return (
                        <>
                            <div key={`${props.name}-work-form-harvest-harvest-${h.id}`} 
                                data-key={`work-form-harvest-harvest-${h.id}`}
                                className="hover:bg-neutral-100 dark:hover:bg-neutral-900"
                            >
                                <FruitInput
                                    required={!props.isTemplate}
                                    value={h.fruit}
                                    onChange={v => setHarvest({ fruit: v }, i)}
                                />
                                <Input
                                    name="kg"
                                    type="number"
                                    value={h.kg?.toString()}
                                    required={!props.isTemplate}
                                    onChange={v => setHarvest({ kg: v }, i)}
                                    label="fields.kg.name"
                                />
                                <Input
                                    name="cartQuantity"
                                    type="number"
                                    required={!props.isTemplate}
                                    value={h.cartQuantity?.toString()}
                                    onChange={v => setHarvest({ cartQuantity: v }, i)}
                                    label="fields.cart-quantity.name"
                                />
                                <FruitCategoryMultiselectInput
                                    required={!props.isTemplate}
                                    fruitId={h.fruit?.id}
                                    value={h.fruitCategories}
                                    onChange={v => setHarvest({ fruitCategories: v }, i)}
                                />
                                <FruitGroupMultiselectInput
                                    required={!props.isTemplate}
                                    fruitId={h.fruit?.id}
                                    value={h.fruitGroup}
                                    onChange={v => setHarvest({ fruitGroup: v }, i)}
                                />
                                <CheckboxInput
                                    label="fields.hide-from-multi-update.name"
                                    name="hideFromMultiUpdate"  
                                    value={h.hideFromMultiUpdate || false}
                                    onChange={v => setHarvest({ hideFromMultiUpdate: v }, i)}
                                />
                                <div className="w-full text-right">
                                    <Button title={t('button.remove')} theme="link" className="text-danger mt-4" onClick={() => removeHarvests(i)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </div>
                            </div>
                            <hr />
                        </>
                    );
                })}
                <Button title={t('button.add')} className="mt-1" onClick={addHarvest}>
                    <FontAwesomeIcon icon={faPlus} />
                    <HTMLCode />
                    <Translate i18nKey="button.add" />
                    <HTMLCode />
                    <Translate i18nKey="fields.harvest.name" transform="lowercase" />
                </Button>
            </fieldset>
            <fieldset>
                <h2 className={`${workerError ? 'text-danger' : ''}`}>Workers</h2>
                <hr />
                {model.workers.map((w, i) => {
                    return (
                        <>
                            <div key={`${props.name}-work-form-page-worker${w.id}`} 
                                data-key={`work-form-page-worker${w.id}`}
                                className="flex gap-4 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                            >
                                <PersonDropdown
                                    required
                                    value={model.workers[i].person}
                                    onChange={v => setWorker({ person: v }, i)}
                                    addPerson
                                />
                                <Input
                                    type="number"
                                    required={!props.isTemplate}
                                    name="price"
                                    label="fields.price.name"
                                    value={model.workers[i].price?.toString()}
                                    onChange={v => setWorker({ price: v }, i)}
                                />
                                <Button title={t('button.remove')} theme="link" className="text-danger mt-4" onClick={() => removeWorker(i)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </div>
                            <hr />
                        </>
                    );
                })}
                <Button title={t('button.add')} className="mt-2" onClick={addWorker}>
                    <FontAwesomeIcon icon={faPlus} />
                    <HTMLCode />
                    <Translate i18nKey="button.add" />
                    <HTMLCode />
                    <Translate i18nKey="fields.workers.name" transform="lowercase" />
                </Button>
            </fieldset>
            <fieldset>
                <Textarea label="fields.note.name" name="note" value={model.note} onChange={v => setModel({ ...model, note: v })} />
            </fieldset>
            <FormActions />
        </Form>
    );
};

export default HarvestFormFields;
